import React, { useRef, useState, useEffect } from 'react';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import styles from './SignupForm.module.css';
import PasswordInput from '../../components/PasswordInput/PasswordInput';
import { FaCheckCircle } from "react-icons/fa";

const USER_REGEX = /^[a-zA-Z][a-zA-Z0-9-_]{3,23}$/;
const PWD_REGEX = /^(?=.*[A-Za-z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SIGNUP_URL = `${BACKEND_URL}/api/v1/users`;

const SignupForm = () => {
  const userRef = useRef();
  const errRef = useRef();

  const [user, setUser] = useState('');
  const [validName, setValidName] = useState(false);

  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);

  const [pwd, setPwd] = useState('');
  const [validPwd, setValidPwd] = useState(false);

  const [matchPwd, setMatchPwd] = useState('');
  const [validMatch, setValidMatch] = useState(false);

  const [possibleSports, setPossibleSports] = useState([])
  const [sport, setSport] = useState("");

  const [displayName, setDisplayName] = useState("");
  const [validDisplayName, setValidDisplayName] = useState(false);

  const [schoolName, setSchoolName] = useState("");
  const [validSchoolName, setValidSchoolName] = useState(false);

  const [mascot, setMascot] = useState("");
  const [validMascot, setValidMascot] = useState(false);

  const [possibleDivisions] = useState(["Division I", "Division II", "Division III"])
  const [division, setDivision] = useState("");

  const [errMsg, setErrMsg] = useState('');
  const [success, setSuccess] = useState(false);

  const [usernameError, setUsernameError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchSportInfo = async () =>{
      const sports = await api.get(`/sports`);
      setPossibleSports(sports.data)
    };
    fetchSportInfo();
  }, []);

  useEffect(() => {
    if (userRef.current){
        userRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setValidName(USER_REGEX.test(user));
  }, [user]);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(email));
  }, [email]);

  useEffect(() => {
    setValidPwd(PWD_REGEX.test(pwd));
    setValidMatch(pwd === matchPwd);
  }, [pwd, matchPwd]);
  
  useEffect(() => {
    setValidDisplayName(displayName.trim().length > 0);
  }, [displayName]);

  useEffect(() => {
    setValidSchoolName(schoolName.trim().length > 0);
  }, [schoolName]);

  useEffect(() => {
    setValidMascot(mascot.trim().length > 0);
  }, [mascot]);

  useEffect(() => {
    setErrMsg('');
  }, [user, email, pwd, matchPwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset error values
    setUsernameError(false);
    setEmailError(false);

    if (
      !validName ||
      !validPwd ||
      !validMatch ||
      !validEmail ||
      !validDisplayName ||
      !validSchoolName ||
      !validMascot ||
      !sport ||
      !division
    ) {
      setErrMsg('Invalid entry');
      return;
    }

    try {
      const response = await api.post(
        SIGNUP_URL,
        JSON.stringify({
          username: user,
          user_password: pwd,
          email: email,
          display_name: displayName,
          school_name: schoolName,
          mascot: mascot,
          division: division,
          sport: sport
        }),
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );

      setSuccess(true);
      const accessToken = response?.data?.accessToken;
      localStorage.setItem('jwtToken', accessToken);
      navigate('/recruits');
    } catch (err) {
      if (!err?.response) {
        setErrMsg('No server response.');
      } else if (err.response?.status === 409) {
        const errorMessage = err.response.data.error;
        setErrMsg(err.response.data.error);
        
        // For error styling later
        if (errorMessage.includes("Username")){
          setUsernameError(true);
        }
        else{
          setEmailError(true);
        }

      } else {
        setErrMsg('Registration failed.');
      }
      errRef.current.focus();
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <section className={styles.signUpBox}>

        {success ? (
          <div className={styles.successBox}>
			<h1>Success!</h1>
			<p className={styles.signUpSubtitle}>Your account has been created.</p>
            <FaCheckCircle className = {styles.checkMark}/>
			<button 
				type="button" className={styles.signInButton}
				onClick = {() => navigate('/login')}
			>
				<span className={styles.signInText}>Sign in →</span>
			</button>
          </div>
        ) : (
		<>
		  <h1 className={styles.signUpTitle}>Sign Up</h1>
		  <p className={styles.signUpSubtitle}>Create your account below</p>

          <form onSubmit={handleSubmit} className={styles.signUpForm}>
            {/*Error message*/}
            <p ref={errRef} className={errMsg ? styles.errorMsg : "offscreen"} aria-live="assertive">{errMsg}</p>

            {/* Username */}
            <div className={styles.inputGroup}>
              <label htmlFor="username" className={styles.inputLabel}>
                Username
              </label>
              <input
                type="text"
                id="username"
                ref={userRef}
                placeholder="example123"
                autoComplete="username"
                onChange={(e) => setUser(e.target.value)}
                value={user}
                required
                className={`${styles.inputField} ${user && (!validName || usernameError) ? styles.inputError : ''}`}
                aria-invalid={!validName}
              />

              {!validName && user && (
                <p className={styles.instructions}>
                  4–24 characters, start with a letter, may include numbers,
                  underscores, or hyphens.
                </p>
              )}
            </div>

            {/* Email */}
            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.inputLabel}>
                Email
              </label>
              <input
                type="email"
                id="email"
                placeholder="example@gmail.com"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                required
                className={`${styles.inputField} ${email && (!validEmail || emailError) ? styles.inputError : ''}`}
                aria-invalid={!validEmail}
              />
              {!validEmail && email && (
                <p className={styles.instructions}>Enter a valid email.</p>
              )}
            </div>

            {/*Password*/}
            <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.inputLabel}>Password</label>
                <PasswordInput
                    id = "password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoComplete = "new-password"
                    error={pwd && !validPwd}
                    instructions = "Password must be 8-24 characters, include letters, numbers, and special characters"
                    placeHolder = "password123!"
                    required 
                />
            </div>

            {/*Confirm Password*/}
            <div className={styles.inputGroup}>
                <label htmlFor="confirm_pwd" className={styles.inputLabel}>Confirm Password</label>
                <PasswordInput
                    id = "confirm_password"
                    value={matchPwd}
                    onChange={(e) => setMatchPwd(e.target.value)}
                    autoComplete = "new-password"
                    error={matchPwd && !validMatch}
                    instructions = "Must match password"
                    placeHolder = "password123!"
                    required 
                />
            </div>

            <hr style={{ borderTop: "1px solid lightgrey", width: "100%", marginBottom: "20px"}} />

            {/* Sport */}
            <div className={styles.inputGroup}>
              <label  className={styles.inputLabel}>
                Sport
              </label>

              <select
                id="sport"
                className={styles.inputField}
                name={"sport"} 
                value={sport}
                required
                onChange={(e) => setSport(e.target.value)}
              >
                <option value="" disabled>
                  Select a sport
                </option>
                {possibleSports.map((sport, index) => (
                  <option key={index} value={sport.sport_name}>
                    {sport.sport_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Name */}
            <div className={styles.inputGroup}>
              <label  className={styles.inputLabel}>
                Display Name <span className={styles.inputSubLabel}>(shown to players)</span>
              </label>
              <input
                type="text"
                id="display_name"
                placeholder="Coach Johnson"
                autoComplete="off"
                onChange={(e) => setDisplayName(e.target.value)}
                value={displayName}
                required
                className={`${styles.inputField} ${displayName && !validDisplayName ? styles.inputError : ''}`}
                aria-invalid={!validDisplayName}
              />
            </div>

            {/* School Name */}
            <div className={styles.inputGroup}>
              <label  className={styles.inputLabel}>
                School Name
              </label>
              <input
                type="text"
                id="school_name"
                placeholder="Example University"
                autoComplete="off"
                onChange={(e) => setSchoolName(e.target.value)}
                value={schoolName}
                required
                className={`${styles.inputField} ${schoolName && !validSchoolName ? styles.inputError : ''}`}
                aria-invalid={!validSchoolName}
              />
            </div>

            {/* Mascot */}
            <div className={styles.inputGroup}>
              <label  className={styles.inputLabel}>
                Mascot
              </label>
              <input
                type="text"
                id="mascot"
                placeholder="Exemplifiers"
                autoComplete="off"
                onChange={(e) => setMascot(e.target.value)}
                value={mascot}
                required
                className={`${styles.inputField} ${mascot && !validMascot ? styles.inputError : ''}`}
                aria-invalid={!validMascot}
              />
            </div>

            {/* Division */}
            <div className={styles.inputGroup}>
              <label  className={styles.inputLabel}>
                Division
              </label>

              <select
                id="division"
                className={styles.inputField}
                name={"division"} 
                value={division}
                required
                onChange={(e) => setDivision(e.target.value)}
              >
                <option value="" disabled>
                  Select a division
                </option>
                {possibleDivisions.map((d, index) => (
                  <option key={index} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={
                !validName ||
                !validPwd ||
                !validMatch ||
                !validEmail ||
                !validDisplayName ||
                !validSchoolName ||
                !validMascot ||
                !sport ||
                !division
              }
              className={styles.signUpButton}
            >
              <span className={styles.signUpButtonText}>Sign Up →</span>
            </button>
            {/* Extra links */}
            <div className={styles.extraLinks}>
                <p>Already have an account? <a href="/login">Sign in</a></p>
            </div>
          </form>
		  </>
        )}

      </section>
    </div>
  );
};

export default SignupForm;